// contexts/AuthContext.tsx
// Replacement for Refine's auth system using our custom authProvider logic
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../lib/util/axios';
import { API_URL } from '../config';

interface User {
    _id: string;
    email: string;
    name: string;
    role: string;
    tutorProgram?: string;
    [key: string]: any;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (credentials: { email: string; password: string }) => Promise<{ success: boolean; error?: string }>;
    logout: () => Promise<void>;
    checkAuth: () => Promise<boolean>;
}

export const normalizeRoleName = (role: unknown): string => {
    if (!role) return "";
    if (typeof role === "string") return role.trim().toLowerCase();
    if (typeof role === "object") {
        const record = role as { name?: unknown; role?: unknown };
        return normalizeRoleName(record.name || record.role);
    }
    return "";
};

export const isTutorAreaRole = (role: unknown): boolean =>
    ["tutor", "instructor", "program_instructor", "program_support"].includes(normalizeRoleName(role));

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const checkAuth = useCallback(async (): Promise<boolean> => {
        const token = localStorage.getItem("accessToken");
        if (!token) {
            setUser(null);
            setIsLoading(false);
            return false;
        }

        // Check if we have stored user data in localStorage
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
                setIsLoading(false);
                return true;
            } catch (e) {
                // Invalid stored user data
            }
        }

        // No stored user but have token - trust the token for now
        // User data will be set on next login
        setIsLoading(false);
        return true;
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (credentials: { email: string; password: string }) => {
        try {
            const response = await axiosInstance.post(`${API_URL}/api/auth/login`, credentials);

            // API returns 'token' not 'accessToken'
            if (response.data.token) {
                localStorage.setItem("accessToken", response.data.token);
                if (response.data.refreshToken) {
                    localStorage.setItem("refreshToken", response.data.refreshToken);
                }

                // Use user data from login response directly
                if (response.data.user) {
                    const userData = {
                        _id: response.data.user.id,
                        email: response.data.user.email,
                        name: `${response.data.user.firstName} ${response.data.user.lastName}`,
                        role: response.data.user.role,
                        ...response.data.user
                    };
                    setUser(userData);
                    localStorage.setItem("user", JSON.stringify(userData));
                }

                return { success: true };
            }
            return { success: false, error: "Login failed" };
        } catch (error: any) {
            const message = error.response?.data?.message || "Invalid credentials";
            return { success: false, error: message };
        }
    };

    const logout = async () => {
        try {
            await axiosInstance.post(`${API_URL}/api/auth/logout`);
        } catch (error) {
            // Ignore logout errors
        } finally {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("user");
            setUser(null);
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user,
                isLoading,
                login,
                logout,
                checkAuth
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

// Replacement hooks for Refine auth hooks
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

// Replacement for useGetIdentity
export const useGetIdentity = () => {
    const { user, isLoading } = useAuth();
    return { data: user, isLoading };
};

// Replacement for useLogin
export const useLogin = () => {
    const { login } = useAuth();
    const navigate = useNavigate();

    const mutate = async (credentials: { email: string; password: string }) => {
        const result = await login(credentials);
        if (result.success) {
            const storedUser = localStorage.getItem("user");
            let role = "";
            if (storedUser) {
                try {
                    const parsed = JSON.parse(storedUser);
                    role = normalizeRoleName(parsed.role);
                } catch (e) {
                    role = "";
                }
            }
            navigate(isTutorAreaRole(role) ? '/tutor/dashboard' : '/admin/dashboard');
        }
        return result;
    };

    return { mutate };
};

// Replacement for useLogout
export const useLogout = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();

    const mutate = async () => {
        await logout();
        navigate('/team');
    };

    return { mutate };
};

// Replacement for useNavigation
export const useNavigation = () => {
    const navigate = useNavigate();

    return useMemo(() => ({
        push: (path: string) => navigate(path),
        replace: (path: string) => navigate(path, { replace: true }),
        go: (delta: number) => navigate(delta),
        goBack: () => navigate(-1),
        list: (resource: string) => navigate(`/admin/${resource}`),
        show: (resource: string, id: string) => navigate(`/admin/${resource}/${id}`),
        edit: (resource: string, id: string) => navigate(`/admin/${resource}/${id}/edit`),
        create: (resource: string) => navigate(`/admin/${resource}/create`),
    }), [navigate]);
};

export default AuthContext;
